import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Grid,
  Input,
  InputNumber,
  message,
  Modal,
  PageHeader,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch, useSelector } from 'react-redux';
import {
  DollarOutlined,
  MinusCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import DescriptionsItem from 'antd/lib/descriptions/Item';
import { OrderAddress } from '../../../custom_types/address-page';
import { Country, COUNTRY_NAMES } from '../../../shared/utils/constants';
import {
  fetchsClientAccountsHandler,
  selectClientAccounts
} from '../../../redux/settings/carriersSlice';
import {
  checkRateHanlder,
  selectLoading,
  selectRates
} from '../../../redux/rate.slice';
import { UserShippingRateRequest } from '../../../custom_types/redux-types';
import { selectCurUser } from '../../../redux/user/userSlice';

interface LabelUrlItem {
  labelUrl: string;
}

interface Labels {
  trackingId: string;
}

interface SuccessResponseData {
  trackingNumber?: string;
  labelUrlList?: LabelUrlItem[];
  labels?: Labels[];
  // 可以根据实际 API 响应添加更多字段
  [key: string]: any; // 允许其他字段
}

// 修复枚举命名规范，改为 PascalCase
enum UnitMeasure {
  LbIn = 0,
  LbKg = 1
}

const CreateOrderPage = () => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const clientAccounts = useSelector(selectClientAccounts);
  const user = useSelector(selectCurUser);

  // 直接从当前用户信息中获取 token
  const clientToken = user.apiToken;

  // 添加提交状态，防止重复提交
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 添加弹框状态和成功数据状态
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [successData, setSuccessData] = useState<SuccessResponseData | null>(
    null
  );
  // 分别用于包裹信息和申报信息的动态添加物品行
  const [packageRows, setPackageRows] = useState([{ key: 0 }]);
  const [invoiceRows, setInvoiceRows] = useState([{ key: 0 }]);

  // 添加包裹信息行
  const addPackageRow = () => {
    setPackageRows([...packageRows, { key: packageRows.length }]);
  };

  // 移除包裹信息行
  const removePackageRow = (index: number) => {
    const newRows = [...packageRows];
    newRows.splice(index, 1);
    setPackageRows(newRows);
  };

  // 添加申报信息行
  const addInvoiceRow = () => {
    setInvoiceRows([...invoiceRows, { key: invoiceRows.length }]);
  };

  // 移除申报信息行
  const removeInvoiceRow = (index: number) => {
    const newRows = [...invoiceRows];
    newRows.splice(index, 1);
    setInvoiceRows(newRows);
  };

  useEffect(() => {
    dispatch(fetchsClientAccountsHandler());
    // 在组件挂载时设置表单的 token 值
    if (clientToken) {
      form.setFieldsValue({
        token: clientToken
      });
    }
  }, [dispatch, form, clientToken]);

  // 重置表单
  const resetForm = () => {
    form.resetFields();
    // 重置后重新设置 token 值
    if (clientToken) {
      form.setFieldsValue({
        token: clientToken
      });
    }
    setPackageRows([{ key: 0 }]);
    setInvoiceRows([{ key: 0 }]);
  };

  // 处理确定按钮点击
  const handleSuccessModalOk = () => {
    setIsSuccessModalVisible(false);
    resetForm();
  };

  const OnSubmit = () => {
    // 如果正在提交中，直接返回，防止重复提交
    if (isSubmitting) {
      message.warning('请勿重复提交');
      return;
    }

    form
      .validateFields()
      .then(async (values) => {
        // 设置提交状态为true
        setIsSubmitting(true);

        try {
          // 处理包裹信息
          const packageList1 =
            values.packageItems?.map(
              (item: {
                itemNam: any;
                totalValue: any;
                quantity: any;
                weights: any;
                length: any;
                width: any;
                height: any;
                unitMeasure: any;
              }) => ({
                weight: Number(item.weights),
                lineItems: [
                  {
                    name: item.itemNam,
                    totalValue: 100,
                    quantity: 1
                  }
                ],
                // 添加尺寸信息（如果有）
                ...(item.length && { length: Number(item.length) }),
                ...(item.width && { width: Number(item.width) }),
                ...(item.height && { height: Number(item.height) }),
                ...(item.unitMeasure && {
                  unitMeasure: Number(item.unitMeasure)
                })
              })
            ) || [];

          // 处理申报信息
          const invoiceDetailList1 =
            values.invoiceItems?.map(
              (item: {
                itemName: any;
                unitPrice: any;
                weight: any;
                unitCount: any;
              }) => ({
                descriptionEn: item.itemName,
                currencyValue: Number(item.unitPrice),
                weight: 0.01, // 使用默认值以防未填写
                unitCount: Number(item.unitCount)
              })
            ) || [];

          // 确保 token 有值，如果没有从表单获取，就使用 clientToken
          console.log(clientToken);
          const apiData = {
            token: clientToken,
            channelId: values.channel,
            orderId: values.receiverId,
            test: false,
            packageType: '02',
            shipTo: {
              name: values.receiverName,
              phone: values.receiverPhone,
              email: values.receiverEmail || '',
              address1: values.receiverAddress1,
              address2: values.receiverAddress2 || '',
              city: values.receiverCity,
              state: values.receiverProvince,
              country: values.country,
              zipCode: values.receiverZip
            },
            // 包裹信息
            packageList: packageList1,
            // 申报信息
            invoice: {
              currencyCode: 'USD',
              invoiceDetailList: invoiceDetailList1
            }
          };
          console.log(apiData);
          // 发送API请求
          const response = await fetch(
            'https://api.eksshipping.com/api/label',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(apiData)
            }
          );

          const result = await response.json();

          if (response.ok) {
            // 保存成功数据并显示弹框
            setSuccessData(result);
            setIsSuccessModalVisible(true);
          } else {
            message.error(`订单创建失败: ${result.message || '未知错误'}`);
          }
        } catch (error) {
          console.error('提交出错:', error);
          message.error('提交过程中出现错误');
        } finally {
          // 无论成功或失败，都重置提交状态
          setIsSubmitting(false);
        }
      })
      .catch((errorInfo) => {
        // 表单验证失败时也要重置提交状态
        console.log('表单验证失败:', errorInfo);
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Form form={form} layout="vertical">
        <Row gutter={10}>
          <Col span={12}>
            <Form.Item name="receiverId" label="订单号">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverName" label="联系人">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverPhone" label="电话">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverEmail" label="邮箱">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverAddress1" label="地址1">
              <Input.TextArea placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverAddress2" label="地址2">
              <Input.TextArea placeholder="请输入" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="物流渠道"
              name="channel"
              rules={[{ required: true, message: '需要物流渠道' }]}
            >
              <Select>
                {clientAccounts.map((account) => (
                  <Select.Option key={account.id} value={account.accountId}>
                    {account.accountId}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="国家"
              name="country"
              initialValue={Country.USA}
              rules={[{ required: true, message: '需要国家' }]}
            >
              <Select>
                <Select.Option value={Country.USA}>
                  {COUNTRY_NAMES[Country.USA]}
                </Select.Option>
                <Select.Option value={Country.CHINA}>
                  {COUNTRY_NAMES[Country.CHINA]}
                </Select.Option>
                <Select.Option value={Country.AUSTRALIA}>
                  {COUNTRY_NAMES[Country.AUSTRALIA]}
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="receiverProvince" label="省份">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverCity" label="城市">
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item name="receiverZip" label="邮编">
              <Input placeholder="请输入" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="包裹信息">
          <Table
            dataSource={packageRows}
            columns={[
              {
                title: '物品名称',
                dataIndex: 'itemNam',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`packageItems`, index, `itemNam`]}
                    rules={[{ required: true, message: '请输入物品名称' }]}
                  >
                    <Input placeholder="请输入" />
                  </Form.Item>
                )
              },
              {
                title: '总重量',
                dataIndex: 'weights',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`packageItems`, index, `weights`]}
                    rules={[{ required: true, message: '请输入总重量' }]}
                  >
                    <InputNumber
                      min={0.01}
                      step={0.01}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '长',
                dataIndex: 'length',
                render: (_, record, index) => (
                  <Form.Item name={[`packageItems`, index, `length`]}>
                    <InputNumber
                      min={0}
                      step={0.1}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '宽',
                dataIndex: 'width',
                render: (_, record, index) => (
                  <Form.Item name={[`packageItems`, index, `width`]}>
                    <InputNumber
                      min={0}
                      step={0.1}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '高',
                dataIndex: 'height',
                render: (_, record, index) => (
                  <Form.Item name={[`packageItems`, index, `height`]}>
                    <InputNumber
                      min={0}
                      step={0.1}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '单位',
                dataIndex: 'unitMeasure',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`packageItems`, index, `unitMeasure`]}
                    initialValue={UnitMeasure.LbIn}
                  >
                    <Select style={{ width: '100%' }}>
                      <Select.Option value={UnitMeasure.LbIn}>
                        lb,in
                      </Select.Option>
                      <Select.Option value={UnitMeasure.LbKg}>
                        lb,kg
                      </Select.Option>
                    </Select>
                  </Form.Item>
                )
              },
              {
                title: '操作',
                render: (_, record, index) => (
                  <Button
                    type="primary"
                    onClick={() => removePackageRow(index)}
                    disabled={packageRows.length === 1}
                  >
                    删除
                  </Button>
                )
              }
            ]}
            pagination={false}
            footer={() => (
              <Button
                type="dashed"
                onClick={addPackageRow}
                icon={<PlusOutlined />}
              >
                添加物品
              </Button>
            )}
          />
        </Form.Item>
        <Form.Item label="申报信息">
          <Table
            dataSource={invoiceRows}
            columns={[
              {
                title: '英文品名',
                dataIndex: 'itemName',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`invoiceItems`, index, `itemName`]}
                    rules={[{ required: true, message: '请输入英文品名' }]}
                  >
                    <Input placeholder="请输入" />
                  </Form.Item>
                )
              },
              {
                title: '单价',
                dataIndex: 'unitPrice',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`invoiceItems`, index, `unitPrice`]}
                    rules={[{ required: true, message: '请输入单价' }]}
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '数量',
                dataIndex: 'unitCount',
                render: (_, record, index) => (
                  <Form.Item
                    name={[`invoiceItems`, index, `unitCount`]}
                    rules={[{ required: true, message: '请输入数量' }]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )
              },
              {
                title: '操作',
                render: (_, record, index) => (
                  <Button
                    type="primary"
                    onClick={() => removeInvoiceRow(index)}
                    disabled={invoiceRows.length === 1}
                  >
                    删除
                  </Button>
                )
              }
            ]}
            pagination={false}
            footer={() => (
              <Button
                type="dashed"
                onClick={addInvoiceRow}
                icon={<PlusOutlined />}
              >
                添加物品
              </Button>
            )}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            onClick={OnSubmit}
            loading={isSubmitting} // 添加加载状态
            disabled={isSubmitting} // 提交时禁用按钮
          >
            {isSubmitting ? '提交中...' : '提交'}
          </Button>
        </Form.Item>
      </Form>

      {/* 成功弹框 */}
      <Modal
        title="订单创建成功"
        visible={isSuccessModalVisible}
        onOk={handleSuccessModalOk}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText="确定"
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>您的订单已成功创建！</p>
        </div>
        {successData && (
          <div>
            <p>
              <strong>跟踪号码:</strong>
              {successData.labels && successData.labels.length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {successData.labels.map((label) => (
                    <div key={label.trackingId} style={{ marginBottom: '4px' }}>
                      <a
                        href={label.trackingId}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: '8px' }}
                      >
                        {label.trackingId}
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                'N/A'
              )}
            </p>
            <p>
              <strong>标签URL:</strong>
              {successData.labelUrlList &&
              successData.labelUrlList[0]?.labelUrl ? (
                <a
                  href={successData.labelUrlList[0].labelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: '8px' }}
                >
                  {successData.labelUrlList[0].labelUrl}
                </a>
              ) : (
                'N/A'
              )}
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CreateOrderPage;
